/**
 * Invoice Service
 * Handles API calls for invoice operations
 */

import { api as apiClient } from './apiClient';

export interface InvoiceItem {
  id?: number;
  productId: number;
  productName?: string;
  hsnCode?: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
  discountPercent?: number;
  gstRate: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  lineTotal?: number;
  unitPriceFormatted?: string;
  cgstAmountFormatted?: string;
  sgstAmountFormatted?: string;
  igstAmountFormatted?: string;
  lineTotalFormatted?: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  customerId: number;
  companyName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  customerGstin?: string;
  salesOrderId?: number;
  orderNumber?: string;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  paidAmount: number;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'PARTIALLY_PAID';
  createdAt: string;
  updatedAt: string;
  items?: InvoiceItem[];
  payments?: PaymentRecord[];

  // Formatted currency fields
  subtotalFormatted?: string;
  cgstAmountFormatted?: string;
  sgstAmountFormatted?: string;
  igstAmountFormatted?: string;
  totalAmountFormatted?: string;
  paidAmountFormatted?: string;
  balanceFormatted?: string;
}

export interface InvoiceFilters {
  customerId?: number;
  status?: string;
  fromDate?: string;
  toDate?: string;
}

export interface CreateInvoiceRequest {
  customerId: number;
  invoiceDate: string;
  paymentTerms?: number;
  items: {
    productId: number;
    quantity: number;
    unitPrice: number;
    discountPercent?: number;
    gstRate: number;
    // Accepted (and persisted) by Sales.createManualInvoice alongside the
    // fields above, even though they weren't part of this type before.
    hsnCode?: string;
    description?: string;
    warehouseId?: number;
    batchId?: number;
    serialNo?: string;
  }[];
  notes?: string;
  // Credit-limit enforcement (backend Sales.assertCreditLimit): omit or false
  // to have an over-limit invoice rejected with a structured 422
  // CREDIT_LIMIT_EXCEEDED error; true to proceed anyway (audit-logged
  // server-side via logBusinessOperation('override_credit_limit', ...)).
  overrideCreditCheck?: boolean;
  // TDS / other withholding — computed and posted as a real GL split
  // (1300 AR net / 1350 TDS Receivable) when tdsApplicable is true.
  tdsApplicable?: boolean;
  tdsSectionCode?: string;
  tdsRate?: number;
  // Export tax treatment (gap E). isExport=true makes the backend apply
  // export GST: LUT/SEZ_WOP -> zero-rated (no GST); WITH_IGST/SEZ_WP/DEEMED
  // -> full IGST. Omitted/false = ordinary domestic invoice.
  isExport?: boolean;
  exportType?: ExportType;
  // Ship-from warehouse: stock is validated against it at creation and the
  // invoice is costed/relieved from it (else the tenant default warehouse).
  warehouseId?: number;
}

/** Export tax character understood by the backend (utils/gst.calculateExportGST). */
export type ExportType = 'LUT' | 'WITH_IGST' | 'SEZ_WOP' | 'SEZ_WP' | 'DEEMED';

export interface CreateInvoiceFromOrderRequest {
  invoiceDate: string;
  paymentTerms?: number;
  // Omit to bill the full remaining order (default/legacy behaviour).
  // Provide to bill a specific subset/quantity per Sales-Order line —
  // "already billed" is enforced server-side via a live SUM query, so
  // over-billing beyond ordered qty is always rejected.
  items?: {
    salesOrderItemId: number;
    quantity?: number;
    warehouseId?: number;
    batchId?: number;
    serialNo?: string;
  }[];
  overrideCreditCheck?: boolean;
  tdsApplicable?: boolean;
  tdsSectionCode?: string;
  tdsRate?: number;
  isExport?: boolean;
  exportType?: ExportType;
  warehouseId?: number;
}

export interface CreateInvoiceFromDeliveryRequest {
  invoiceDate: string;
  paymentTerms?: number;
  overrideCreditCheck?: boolean;
  tdsApplicable?: boolean;
  tdsSectionCode?: string;
  tdsRate?: number;
  isExport?: boolean;
  exportType?: ExportType;
}

/** Structured body of a 422 CREDIT_LIMIT_EXCEEDED rejection (Sales.assertCreditLimit). */
export interface CreditLimitExceededDetails {
  code: 'CREDIT_LIMIT_EXCEEDED';
  creditLimit: number;
  outstanding: number;
  currentInvoiceAmount: number;
  projectedExposure: number;
  excess: number;
}

export interface UpdateInvoiceStatusRequest {
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
}

export type PaymentMethod = 'CASH' | 'CHEQUE' | 'BANK_TRANSFER' | 'UPI' | 'CARD';

export interface RecordPaymentRequest {
  paymentAmount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
}

export interface PaymentRecord {
  id: number;
  invoiceId: number;
  paymentAmount: number;
  paymentAmountFormatted: string;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  notes?: string;
  createdBy?: number;
  recordedByName?: string;
  createdAt: string;
}

export interface CompanySettings {
  companyName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  gstin: string;
  bankName: string;
  bankAccountNumber: string;
  bankIfscCode: string;
  bankBranch: string;
}

// Helper to normalize invoice data (MySQL returns decimals as strings)
const normalizeInvoice = (invoice: any): Invoice => ({
  ...invoice,
  totalAmount: parseFloat(String(invoice.totalAmount)) || 0,
  paidAmount: parseFloat(String(invoice.paidAmount)) || 0,
  subtotal: parseFloat(String(invoice.subtotal)) || 0,
  cgstAmount: parseFloat(String(invoice.cgstAmount)) || 0,
  sgstAmount: parseFloat(String(invoice.sgstAmount)) || 0,
  igstAmount: parseFloat(String(invoice.igstAmount)) || 0,
});

class InvoiceService {
  /**
   * Get all invoices with optional filters
   */
  async getInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams();
    
    if (filters?.customerId) {
      params.append('customerId', filters.customerId.toString());
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.fromDate) {
      params.append('fromDate', filters.fromDate);
    }
    if (filters?.toDate) {
      params.append('toDate', filters.toDate);
    }

    const queryString = params.toString();
    const url = queryString ? `/sales/invoices?${queryString}` : '/sales/invoices';

    const response = await apiClient.get(url);
    return (response.invoices || []).map(normalizeInvoice);
  }

  /**
   * Get invoice by ID
   */
  async getInvoiceById(id: number): Promise<Invoice> {
    const response = await apiClient.get(`/sales/invoices/${id}`);
    return normalizeInvoice(response.invoice);
  }

  /**
   * Create manual invoice
   */
  async createInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    const response = await apiClient.post('/sales/invoices', data);
    return response.invoice;
  }

  /**
   * Edit a DRAFT invoice (lines/dates/tax/warehouse). Backend rejects with 409
   * once the invoice is sent, posted or paid.
   */
  async updateInvoice(id: number, data: Partial<CreateInvoiceRequest>): Promise<Invoice> {
    const response = await apiClient.put(`/sales/invoices/${id}`, data);
    return response.invoice;
  }

  /**
   * Create invoice from sales order
   */
  async createInvoiceFromOrder(orderId: number, data: CreateInvoiceFromOrderRequest): Promise<Invoice> {
    const response = await apiClient.post(`/sales/orders/${orderId}/invoice`, data);
    return response.invoice;
  }

  /**
   * Create invoice from a Delivery — copies product/qty/warehouse/batch from
   * the delivery's own lines server-side (Sales.createInvoiceFromDelivery),
   * guards against double-billing the same delivery.
   */
  async createInvoiceFromDelivery(deliveryId: number, data: CreateInvoiceFromDeliveryRequest): Promise<Invoice> {
    const response = await apiClient.post(`/deliveries/${deliveryId}/invoice`, data);
    return response.invoice;
  }

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(id: number, data: UpdateInvoiceStatusRequest): Promise<void> {
    await apiClient.put(`/sales/invoices/${id}/status`, data);
  }

  /**
   * Record payment against invoice
   */
  async recordPayment(id: number, data: RecordPaymentRequest): Promise<{
    paymentId: number;
    newPaidAmount: number;
    newStatus: string;
    balance: number;
  }> {
    const response = await apiClient.post(`/sales/invoices/${id}/payment`, data);
    return {
      paymentId: response.paymentId,
      newPaidAmount: response.newPaidAmount,
      newStatus: response.newStatus,
      balance: response.balance
    };
  }

  /**
   * Get payment history for an invoice
   */
  async getPaymentHistory(id: number): Promise<PaymentRecord[]> {
    const response = await apiClient.get(`/sales/invoices/${id}/payments`);
    return response.payments;
  }

  /**
   * Get invoice statistics
   */
  async getInvoiceStats(): Promise<{
    totalInvoices: number;
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    overdueInvoices: number;
  }> {
    // This would be a separate endpoint for dashboard statistics
    const invoices = await this.getInvoices();

    const stats = invoices.reduce(
      (acc, invoice) => {
        acc.totalInvoices += 1;
        acc.totalAmount += invoice.totalAmount;
        acc.paidAmount += invoice.paidAmount;
        acc.pendingAmount += invoice.totalAmount - invoice.paidAmount;

        if (invoice.status === 'OVERDUE') {
          acc.overdueInvoices += 1;
        }

        return acc;
      },
      {
        totalInvoices: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueInvoices: 0
      }
    );

    return stats;
  }

  /**
   * Get company settings for invoice printing
   */
  async getCompanySettings(): Promise<CompanySettings> {
    const response = await apiClient.get('/settings/company');
    return response.settings;
  }
}

export const invoiceService = new InvoiceService();
export default invoiceService;